import CustomInput from 'components/CustomInput/CustomInput';
import React, { useEffect } from 'react';
import { PHONE_NUMBER_LENGTH } from 'utils/const/numbers';
import { translate } from 'components/Translation';
import CustomButton from 'components/CustomButtons/Button';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import CustomTag from 'components/CustomTag/CustomTag';
import styles from 'assets/jss/material-dashboard-pro-react/views/payments/topupStyle';
import { makeStyles } from '@material-ui/styles';
import { useDispatch } from 'react-redux';
import { showLoadingIndicator, hideLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { NumbersService } from 'services/numbers/numbers';
import { finalize } from 'rxjs/operators';

// @ts-ignore
const useStyles = makeStyles(styles);

export interface ICustomNumberValue {
	value: string;
	error: boolean;
	success: boolean;
	loading: boolean;
}

interface ICustomValuesProps {
	customNumberValue: ICustomNumberValue;
	setCustomNumberValue: React.Dispatch<React.SetStateAction<ICustomNumberValue>>;
	customNumbers: string[];
	setCustomNumbers: React.Dispatch<React.SetStateAction<string[]>>;
}

const CustomValues = ({
	customNumberValue,
	setCustomNumberValue,
	customNumbers,
	setCustomNumbers,
}: ICustomValuesProps) => {
	const classes = useStyles();

	const dispatch = useDispatch();

	useEffect(() => {
		if (customNumberValue.loading) {
			dispatch(showLoadingIndicator());
		} else {
			dispatch(hideLoadingIndicator());
		}
	}, [customNumberValue.loading, dispatch]);

	const checkNumber = (number: string) => {
		setCustomNumberValue((customNumberValue) => ({
			...customNumberValue,
			loading: true,
		}));

		const setError = () =>
			setCustomNumberValue((customNumberValue) => ({
				...customNumberValue,
				success: false,
				error: true,
			}));

		NumbersService.getAccountNumberInfo(number)
			.pipe(
				finalize(() => setCustomNumberValue((customNumberValue) => ({ ...customNumberValue, loading: false })))
			)
			.subscribe(
				({ billingType }) => {
					if (billingType === 'PREPAID') {
						setCustomNumberValue((customNumberValue) => ({
							...customNumberValue,
							success: true,
							error: false,
						}));
					} else {
						setError();
					}
				},
				() => setError()
			);
	};

	const isAddCustomNumberDisabled =
		customNumberValue.value.length !== PHONE_NUMBER_LENGTH || customNumberValue.error || customNumberValue.loading;

	return (
		<>
			<div className={classes.customNumbersContainer}>
				<div>
					<CustomInput
						inputProps={{
							value: customNumberValue.value,
							onChange: (event: React.FormEvent<HTMLInputElement>) => {
								let value = (event.target as HTMLInputElement).value;

								value = value.replace(/\D/g, '').substr(0, PHONE_NUMBER_LENGTH);
								setCustomNumberValue((customNumberValue) => ({
									...customNumberValue,
									value,
								}));

								if (value.length === PHONE_NUMBER_LENGTH) {
									checkNumber(value);
								} else {
									setCustomNumberValue((customNumberValue) => ({
										...customNumberValue,
										success: false,
										error: false,
										loading: false,
									}));
								}
							},
							maxLength: PHONE_NUMBER_LENGTH,
						}}
						id="custom-number"
						labelText={translate('payments.topup.customNumberLabel')}
						helperText={customNumberValue.error ? translate('payments.topup.cantAdd') : ''}
						error={customNumberValue.error}
						disabled={customNumberValue.loading}
						success={customNumberValue.success}
						formControlProps={{
							fullWidth: true,
						}}
					/>
				</div>
				<div className={classes.addCustomNumber}>
					<CustomButton
						disabled={isAddCustomNumberDisabled}
						onClick={() => {
							setCustomNumberValue((customNumberValue) => {
								setCustomNumbers((customNumbers) => [
									...new Set([...customNumbers, customNumberValue.value]),
								]);
								return {
									value: '',
									error: false,
									success: false,
									loading: false,
								};
							});
						}}
						color="tertiary-light"
					>
						{translate('add')}
					</CustomButton>
				</div>
			</div>
			<GridContainer>
				<GridItem xs={12} id="custom-numbers" className={classes.allNumbers}>
					{customNumbers.map((number, ind) => (
						<div key={number} className={classes.customNumber}>
							<CustomTag
								className={`custom-number-tag ${classes.numberTag}`}
								onClick={() =>
									setCustomNumbers((customNumbers: Array<string>) =>
										customNumbers.filter((_: string, numInd: number) => ind !== numInd)
									)
								}
								label={number}
							/>
						</div>
					))}
				</GridItem>
			</GridContainer>
		</>
	);
};

export default CustomValues;
